import * as Comlink from 'comlink'
import type { Remote } from 'comlink'
import type {
  ImageWorkerApi,
  ProcessImageOptions,
  ProcessImageWorkerResult,
} from './image-worker'

interface PooledWorker {
  worker: Worker
  proxy: Remote<ImageWorkerApi>
  busy: boolean
}

export class ImageWorkerPool {
  private pool: PooledWorker[] = []
  private maxWorkers: number
  private pendingQueue: Array<{
    resolve: (worker: PooledWorker) => void
  }> = []

  constructor(maxWorkers?: number) {
    this.maxWorkers = maxWorkers ?? navigator.hardwareConcurrency ?? 4
  }

  private createWorker(): PooledWorker {
    const worker = new Worker(new URL('./image-worker.ts', import.meta.url), {
      type: 'module',
    })
    const proxy = Comlink.wrap<ImageWorkerApi>(worker)
    return { worker, proxy, busy: false }
  }

  private getIdleWorker(): PooledWorker | null {
    return this.pool.find((w) => !w.busy) ?? null
  }

  private acquireWorker(): Promise<PooledWorker> {
    // try idle worker
    const idle = this.getIdleWorker()
    if (idle) {
      idle.busy = true
      return Promise.resolve(idle)
    }

    // spawn new if under limit
    if (this.pool.length < this.maxWorkers) {
      const worker = this.createWorker()
      worker.busy = true
      this.pool.push(worker)
      return Promise.resolve(worker)
    }

    // wait for one to become available
    return new Promise((resolve) => {
      this.pendingQueue.push({ resolve })
    })
  }

  private releaseWorker(pooledWorker: PooledWorker): void {
    pooledWorker.busy = false

    // if someone is waiting, give them this worker
    const pending = this.pendingQueue.shift()
    if (pending) {
      pooledWorker.busy = true
      pending.resolve(pooledWorker)
    }
  }

  async processImage(
    blob: Blob,
    opts?: ProcessImageOptions,
  ): Promise<ProcessImageWorkerResult> {
    const pooledWorker = await this.acquireWorker()
    try {
      return await pooledWorker.proxy.processImage(blob, opts)
    } finally {
      this.releaseWorker(pooledWorker)
    }
  }

  terminate(): void {
    for (const { worker } of this.pool) {
      worker.terminate()
    }
    this.pool = []
    this.pendingQueue = []
  }
}

// singleton
export const imagePool = new ImageWorkerPool()
